// components/HotspotEditor.jsx
// Reusable: Member Portal (self-edit) — pakai axios instance `api` dari ../api
//
// Props:
//   silhouetteUrl     - string, URL gambar (motor atau gear person)
//   initialSpecs      - array [{label, x_percent, y_percent, description, photo_url?}]
//   saveEndpoint      - string, path relatif ke api.js baseURL (misal '/member-auth/me/motor-specs')
//   bodyKey           - 'motor_specs' atau 'gear_specs'
//   suggestedLabels   - array string opsional, chip cepat
//   title             - judul section
//   enablePhotoUpload - boolean, kalau true tampilin tombol upload foto per titik (dipakai buat gear)
//   photoUploadEndpoint - path upload foto, default '/upload/gear-photo'

import React, { useState, useRef } from 'react';
import api from '../api';

const MAX_HOTSPOTS = 8;

export default function HotspotEditor({
  silhouetteUrl,
  initialSpecs = [],
  saveEndpoint,
  bodyKey = 'motor_specs',
  suggestedLabels = [],
  title = 'Atur Hotspot',
  enablePhotoUpload = false,
  photoUploadEndpoint = '/upload/gear-photo',
}) {
  const [hotspots, setHotspots] = useState(initialSpecs);
  const [pendingPoint, setPendingPoint] = useState(null);
  const [labelInput, setLabelInput] = useState('');
  const [descInput, setDescInput] = useState('');
  const [photoInput, setPhotoInput] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [dirty, setDirty] = useState(false);
  const containerRef = useRef(null);

  const persist = async (updatedList) => {
    setSaving(true);
    try {
      await api.put(saveEndpoint, { [bodyKey]: updatedList });
      setDirty(false);
      return true;
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal simpan');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleImageClick = (e) => {
    if (hotspots.length >= MAX_HOTSPOTS) return;
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x_percent = Number((((e.clientX - rect.left) / rect.width) * 100).toFixed(2));
    const y_percent = Number((((e.clientY - rect.top) / rect.height) * 100).toFixed(2));
    setPendingPoint({ x_percent, y_percent });
    setLabelInput('');
    setDescInput('');
    setPhotoInput('');
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await api.post(photoUploadEndpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotoInput(res.data.url);
    } catch (err) {
      alert('Gagal upload foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const confirmAddPoint = async () => {
    if (!labelInput.trim() || !pendingPoint) return;
    const newPoint = {
      label: labelInput.trim(),
      x_percent: pendingPoint.x_percent,
      y_percent: pendingPoint.y_percent,
      description: descInput.trim() || null,
      ...(enablePhotoUpload ? { photo_url: photoInput || null } : {})
    };
    const updated = [...hotspots, newPoint];
    const ok = await persist(updated);
    if (ok) {
      setHotspots(updated);
      setPendingPoint(null);
    }
  };

  const deletePoint = async (idx) => {
    if (!window.confirm('Hapus titik ini?')) return;
    const updated = hotspots.filter((_, i) => i !== idx);
    const ok = await persist(updated);
    if (ok) setHotspots(updated);
  };

  const handleDragMove = (idx, e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x_percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y_percent = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setHotspots(prev => prev.map((h, i) => i === idx ? { ...h, x_percent, y_percent } : h));
    setDirty(true);
  };

  const handleDragEnd = async () => {
    if (draggingIdx === null) return;
    setDraggingIdx(null);
    if (dirty) await persist(hotspots);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>{title}</h3>
      <p style={{ color: '#8B92A0', fontSize: 12, marginBottom: 12 }}>
        Titik: {hotspots.length}/{MAX_HOTSPOTS}
        {hotspots.length >= MAX_HOTSPOTS && <span style={{ color: '#D02127' }}> — kuota penuh, hapus salah satu dulu</span>}
        {saving && <span> · menyimpan...</span>}
      </p>

      {silhouetteUrl && (
        <div
          ref={containerRef}
          onClick={handleImageClick}
          onMouseMove={(e) => { if (draggingIdx !== null) handleDragMove(draggingIdx, e); }}
          onMouseUp={handleDragEnd}
          onMouseLeave={() => { if (draggingIdx !== null) handleDragEnd(); }}
          style={{
            position: 'relative', width: '100%', maxWidth: 500,
            cursor: hotspots.length >= MAX_HOTSPOTS ? 'not-allowed' : 'crosshair',
            border: '1px solid #262b33', borderRadius: 8, overflow: 'hidden', background: '#1C2128'
          }}
        >
          <img src={silhouetteUrl} alt="silhouette" style={{ width: '100%', display: 'block', userSelect: 'none' }} draggable={false} />
          {hotspots.map((h, idx) => (
            <div
              key={idx}
              onMouseDown={(e) => { e.stopPropagation(); setDraggingIdx(idx); }}
              onDoubleClick={(e) => { e.stopPropagation(); deletePoint(idx); }}
              title={`${h.label} — drag geser, double-click hapus`}
              style={{
                position: 'absolute', left: `${h.x_percent}%`, top: `${h.y_percent}%`,
                transform: 'translate(-50%, -50%)', width: 22, height: 22, borderRadius: '50%',
                background: '#D02127', border: '2px solid #F0EDE4', cursor: 'grab',
                boxShadow: '0 2px 6px rgba(0,0,0,0.4)'
              }}
            />
          ))}
        </div>
      )}

      <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
        {hotspots.map((h, idx) => (
          <div key={idx} style={{ background: '#1C2128', border: '1px solid #262b33', borderRadius: 8, padding: 10 }}>
            {enablePhotoUpload && h.photo_url && (
              <div style={{ width: '100%', aspectRatio: '1', background: '#12151A', borderRadius: 6, overflow: 'hidden', marginBottom: 6 }}>
                <img src={h.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <p style={{ fontSize: 12, fontWeight: 700 }}>{h.label}</p>
            {h.description && <p style={{ fontSize: 11, color: '#8B92A0', marginTop: 2 }}>{h.description}</p>}
            <button onClick={() => deletePoint(idx)} style={{ color: '#D02127', background: 'none', border: 'none', fontSize: 11, marginTop: 4, cursor: 'pointer' }}>
              Hapus
            </button>
          </div>
        ))}
      </div>

      {pendingPoint && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#1C2128', padding: 20, borderRadius: 12, width: '90%', maxWidth: 360 }}>
            <h4 style={{ marginBottom: 12 }}>Tandai Titik</h4>

            {suggestedLabels.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {suggestedLabels.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setLabelInput(s)}
                    style={{
                      fontSize: 12, padding: '4px 10px', borderRadius: 999,
                      background: labelInput === s ? '#D02127' : '#262b33',
                      color: labelInput === s ? '#fff' : '#8B92A0', border: 'none', cursor: 'pointer'
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <input
              autoFocus
              placeholder="Label titik (misal: Ban Belakang)"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 6, marginBottom: 8, background: '#12151A', color: '#F0EDE4', border: '1px solid #262b33' }}
            />
            <textarea
              placeholder="Merk/tipe/deskripsi (opsional)"
              value={descInput}
              onChange={(e) => setDescInput(e.target.value)}
              rows={2}
              style={{ width: '100%', padding: 10, borderRadius: 6, marginBottom: 10, background: '#12151A', color: '#F0EDE4', border: '1px solid #262b33' }}
            />

            {enablePhotoUpload && (
              <div style={{ marginBottom: 12 }}>
                {photoInput && (
                  <div style={{ width: 60, height: 60, borderRadius: 6, overflow: 'hidden', marginBottom: 6, background: '#12151A' }}>
                    <img src={photoInput} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <label style={{ display: 'inline-block', background: '#262b33', color: '#F0EDE4', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                  {uploadingPhoto ? 'Mengupload...' : 'Upload Foto (opsional)'}
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                </label>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setPendingPoint(null)} style={{ flex: 1, padding: 10, borderRadius: 6, background: '#262b33', color: '#F0EDE4', border: 'none', cursor: 'pointer' }}>
                Batal
              </button>
              <button
                type="button"
                onClick={confirmAddPoint}
                disabled={saving || !labelInput.trim()}
                style={{ flex: 1, padding: 10, borderRadius: 6, background: '#D02127', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
